import React, { useCallback, useState, useEffect } from "react";
import { Grid, Box, ToggleButtonGroup, ToggleButton, Tooltip } from "@mui/material";
import { intl, DmsPage, fetchData, HTTP_STATUS } from "dmsbasereact";
import { DmsAnalytics, useDmsAnalyticsContext } from "../../components/DmsAnalytics";
import { FlowEquation } from "./components/FlowEquation";

// 3D card styling with beveled edges (visible on white background)
const card3DStyle = {
    borderRadius: 2,
    position: "relative",
    background: "background.paper",
    // Top and left edges - subtle light gray (highlight on white)
    borderTop: "2px solid rgba(0, 0, 0, 0.08)",
    borderLeft: "2px solid rgba(0, 0, 0, 0.06)",
    // Bottom and right edges - darker gray (shadow)
    borderBottom: "3px solid rgba(0, 0, 0, 0.2)",
    borderRight: "3px solid rgba(0, 0, 0, 0.15)",
    // Additional shadow for depth
    boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.12), inset -1px -1px 2px rgba(0, 0, 0, 0.05)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
        transform: "translateY(-2px) translateX(-1px)",
        // Enhanced 3D effect on hover
        borderTop: "2px solid rgba(0, 0, 0, 0.05)",
        borderLeft: "2px solid rgba(0, 0, 0, 0.04)",
        borderBottom: "4px solid rgba(0, 0, 0, 0.25)",
        borderRight: "4px solid rgba(0, 0, 0, 0.2)",
        boxShadow: "6px 6px 12px rgba(0, 0, 0, 0.18), inset -2px -2px 3px rgba(0, 0, 0, 0.08)",
    },
};

const DEFAULT_FLOW_SUMMARY = {
    eurofarma_total: 0,
    distributor_total: 0,
    intermediary_total: 0,
    dispenser_total: 0,
    ministry_health_total: 0,
    dispensation_total: 0,
    balance: 0,
    returns: 0,
};

// Global Metric Toggle Component - changes what values bar charts display
const MetricToggle = ({ value, onChange }) => {
    const handleChange = useCallback((event, newMetric) => {
        if (newMetric !== null) {
            onChange(newMetric);
        }
    }, [onChange]);

    const metrics = [
        {
            value: "sales",
            label: intl.get("Analytics.Metric.Sales").d("Venda"),
            tooltip: intl.get("Analytics.Metric.Sales.Tooltip").d("Quantidade vendida/enviada para cada instituição"),
        },
        {
            value: "balance",
            label: intl.get("Analytics.Metric.Balance").d("Saldo"),
            tooltip: intl.get("Analytics.Metric.Balance.Tooltip").d("Estoque não dispensado que ainda está com a instituição"),
        },
        {
            value: "dispensation",
            label: intl.get("Analytics.Metric.Dispensation").d("Dispensação"),
            tooltip: intl.get("Analytics.Metric.Dispensation.Tooltip").d("Quantidade já dispensada aos pacientes"),
        },
    ];

    return (
        <ToggleButtonGroup
            value={value}
            exclusive
            onChange={handleChange}
            size="small"
            sx={{
                "& .MuiToggleButton-root": {
                    px: 2,
                    py: 0.5,
                    fontSize: "0.8rem",
                    textTransform: "none",
                    fontWeight: 500,
                    borderColor: "primary.main",
                },
                "& .Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "white",
                    "&:hover": {
                        backgroundColor: "primary.dark",
                    },
                },
            }}
        >
            {metrics.map((metric) => (
                <Tooltip key={metric.value} title={metric.tooltip} arrow placement="bottom">
                    <ToggleButton value={metric.value}>
                        {metric.label}
                    </ToggleButton>
                </Tooltip>
            ))}
        </ToggleButtonGroup>
    );
};

const SalesBody = ({ globalMetric }) => {
    const { queryParams, refreshKey } = useDmsAnalyticsContext();
    const [flowSummary, setFlowSummary] = useState(DEFAULT_FLOW_SUMMARY);
    const [loadingFlow, setLoadingFlow] = useState(false);

    const loadFlowSummary = useCallback(async () => {
        setLoadingFlow(true);
        try {
            const response = await fetchData(
                `/analytics/flow-summary?${queryParams}`,
                "GET",
                true
            );
            if (response.status === HTTP_STATUS.OK_200) {
                setFlowSummary(response.data?.data || DEFAULT_FLOW_SUMMARY);
            }
        } catch (err) {
            console.error("Error loading flow summary:", err);
        } finally {
            setLoadingFlow(false);
        }
    }, [queryParams]);

    useEffect(() => {
        loadFlowSummary();
    }, [loadFlowSummary, refreshKey]);

    // Dispensation metric only applies to Product and Batch charts
    // Client and UF charts use sales when dispensation is selected
    const clientUfMetric = globalMetric === "dispensation" ? "sales" : globalMetric;

    return (
        <>
            {/* Flow Summary - always visible */}
            <Box sx={{ mb: 3 }}>
                <FlowEquation data={flowSummary} isLoading={loadingFlow} />
            </Box>

            {/* Bar Charts - values change based on globalMetric */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <DmsAnalytics.Bar
                        url="/analytics/sales/by-product"
                        title={intl.get("Analytics.Sales.ByProduct").d("por Produto")}
                        crossFilter="product"
                        color="primary"
                        labelKey="name"
                        valueKey="value"
                        metric={globalMetric}
                        sx={card3DStyle}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <DmsAnalytics.Bar
                        url="/analytics/sales/by-client"
                        title={intl.get("Analytics.Sales.ByClient").d("por Clientes Diretos")}
                        crossFilter="client"
                        color="info"
                        labelKey="name"
                        valueKey="value"
                        metric={clientUfMetric}
                        sx={card3DStyle}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <DmsAnalytics.Bar
                        url="/analytics/sales/by-batch"
                        title={intl.get("Analytics.Sales.ByBatch").d("por Lote")}
                        crossFilter="batch"
                        color="warning"
                        labelKey="name"
                        valueKey="value"
                        metric={globalMetric}
                        sx={card3DStyle}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <DmsAnalytics.Bar
                        url="/analytics/sales/by-uf"
                        title={intl.get("Analytics.Sales.ByUf").d("Venda por UF/Município Destino")}
                        crossFilter="uf"
                        color="success"
                        labelKey="name"
                        valueKey="value"
                        metric={clientUfMetric}
                        sx={card3DStyle}
                    />
                </Grid>

                {/* Stock Tables */}
                <Grid item xs={12} md={6}>
                    <DmsAnalytics.Table
                        url="/analytics/stock/by-product"
                        title={intl.get("DmsAnalytics.Stock.ByProduct").d("Unidades Produzidas e Estoque por Produto")}
                        columns={[
                            { field: "code", header: intl.get("DmsAnalytics.Stock.ProductCode").d("Product Code") },
                            { field: "name", header: intl.get("DmsAnalytics.Stock.Product").d("Produto") },
                            { field: "produced", header: intl.get("DmsAnalytics.Stock.Produced").d("Unidades Produzidas"), align: "right" },
                            { field: "remaining", header: intl.get("DmsAnalytics.Stock.Remaining").d("Estoque Remanescente"), align: "right" },
                        ]}
                        sx={card3DStyle}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <DmsAnalytics.Table
                        url="/analytics/stock/by-batch"
                        title={intl.get("DmsAnalytics.Stock.ByBatch").d("Unidades Produzidas e Estoque por Lote")}
                        columns={[
                            { field: "name", header: intl.get("DmsAnalytics.Stock.Batch").d("Lote") },
                            { field: "produced", header: intl.get("DmsAnalytics.Stock.Produced").d("Unidades Produzidas"), align: "right" },
                            { field: "remaining", header: intl.get("DmsAnalytics.Stock.Remaining").d("Estoque Remanescente"), align: "right" },
                        ]}
                        sx={card3DStyle}
                    />
                </Grid>
            </Grid>
        </>
    );
};

export const SalesAnalyticsPage = () => {
    const [globalMetric, setGlobalMetric] = useState("sales"); // "sales" = Venda, "balance" = Saldo

    return (
        <DmsPage name={intl.get("Analytics.Sales.Title").d("Vendas Eurofarma")} manual_slug="analytics-sales">
            <DmsAnalytics.Card>
                <DmsAnalytics.Header
                    title={intl.get("Analytics.Sales.Title").d("Vendas Eurofarma")}
                    sx={{
                        backgroundColor: "background.paper",
                        color: "text.primary",
                        border: "none",
                        "& .MuiTypography-root": {
                            color: "text.primary",
                            fontWeight: 700,
                            fontSize: "2rem",
                        },
                        "& .MuiIconButton-root": {
                            color: "primary.main",
                        },
                    }}
                >
                    {/* Metric Toggle in header center */}
                    <MetricToggle value={globalMetric} onChange={setGlobalMetric} />
                </DmsAnalytics.Header>
                <DmsAnalytics.Chips />
                <DmsAnalytics.Body>
                    <SalesBody globalMetric={globalMetric} />
                </DmsAnalytics.Body>
                <DmsAnalytics.Filters />
            </DmsAnalytics.Card>
        </DmsPage>
    );
};
